<template>
  <div>
    <!-- buyerCampaign is shared Vuex state and created() is async, so until the fetched campaign
         matches the route the store may still hold the previously viewed one. Rendering the form
         then would flash another campaign's budget and, worse, let a fast click submit it. -->
    <Panel
      v-if="!isLoaded"
      size="small"
      :loading="true"
      loader-text="Loading Campaign"
    >
      <template #header>
        <span data-cy="heading">Edit Campaign</span>
      </template>
    </Panel>

    <validation-observer
      v-else
      ref="formValidation"
      v-slot="{ handleSubmit, invalid }"
    >
      <b-form
        novalidate
        @submit.stop.prevent="handleSubmit(showConfirmSubmit)"
      >
        <Panel size="small">
          <template #header>
            <span data-cy="heading">Edit Campaign</span>
          </template>

          <template #content>
            <div>
              <b-form-group
                label="Status"
                label-class="pr-4 custom-form-input-label--internal"
                label-cols-sm="3"
                label-cols="12"
                content-cols="12"
                content-cols-sm="5"
                content-cols-lg="4"
                content-cols-xl="3"
                label-align-sm="right"
                label-align="left"
              >
                <div class="form-control-plaintext">
                  <b-badge :variant="getBadgeVariantForCampaignStatus(buyerCampaign)">
                    {{ getStatusText(buyerCampaign) }}
                  </b-badge>
                </div>
              </b-form-group>

              <!-- Category and Language are shown for context but not editable: changing either
                   forces an admin re-review, and a softphone campaign's category is what its
                   provisioned agents were set up against. -->
              <BaseFormSelect
                id="buyer-create-campaign__campaign-category"
                :value="categoryId"
                :horizontal="true"
                internal-platform-input
                :small="true"
                readonly
                name="Category"
                data-cy="select-category"
                label="Category"
                text-field="displayName"
                value-field="id"
                :options="categoryList"
                width="5"
                width-lg="4"
                width-xl="3"
                has-tooltip
              />

              <!-- Mirrors the create page: a category with a single language shows no language
                   dropdown, because there was never a choice to make. -->
              <BaseFormSelect
                v-if="showLanguageSelect"
                id="buyer-create-campaign__campaign-language"
                :value="languageId"
                :horizontal="true"
                internal-platform-input
                :small="true"
                readonly
                name="Language"
                data-cy="select-language"
                label="Language"
                text-field="name"
                value-field="id"
                :options="languageOptions"
                width="5"
                width-lg="4"
                width-xl="3"
                has-tooltip
              />
            </div>
          </template>
        </Panel>

        <Audience softphone />

        <Budget
          :edit="true"
          :invalid="invalid"
          :minimum-initial-deposit-satisfied="isMinimumInitialDepositRequirementSatisfied"
          :loading="isLoading"
          :selected-category="selectedCategory"
          @submit="showConfirmSubmit"
        />
      </b-form>
    </validation-observer>

    <b-modal
      id="update-softphone-campaign-modal"
      ref="update-softphone-campaign-modal"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      title="Confirm Update"
      ok-title="Update"
      button-size="sm"
      @ok="submitForm"
    >
      <UpdateCampaignAlert :changed-sections="changedReviewSections" />

      <p class="mb-0">
        Are you sure you want to update the buyer campaign?
      </p>

      <template #modal-footer>
        <b-button
          type="button"
          variant="secondary"
          size="sm"
          :disabled="isLoading"
          @click="$bvModal.hide('update-softphone-campaign-modal')"
        >
          Cancel
        </b-button>

        <BaseButtonLoader
          :loading="isLoading"
          class="ml-2"
          type="button"
          :custom-button="false"
          size="sm"
          variant="primary"
          @click="submitForm"
        >
          Update
        </BaseButtonLoader>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import cloneDeep from 'lodash.clonedeep'
import isEqual from 'lodash.isequal'

import Audience from './Audience.vue'
import Budget from './Budget.vue'
import UpdateCampaignAlert from './UpdateCampaignAlert.vue'
import { showErrorMessage, showSuccessMessage } from '@/notification-utils'
import marketBudgetUsageMixin from '@/mixins/market-budget-usage.mixin'
import {
  getBadgeVariantForCampaignStatus,
  getStatusText,
  isSoftphoneCampaign
} from '@/store/modules/campaign.store'
import { getSelectableCategories } from '@/utils/campaignCategories'

export default {
  name: 'BuyerEditSoftphoneCampaign',

  // Deliberately not the campaign's own name, which the agent is shown nowhere else: it is
  // generated bookkeeping (see `buildSoftphoneCampaignName`) and names the softphone at that.
  title: 'Edit campaign',

  components: {
    Audience,
    Budget,
    UpdateCampaignAlert,
  },

  mixins: [marketBudgetUsageMixin],

  props: {
    id: {
      type: String,
      default: ''
    },
  },

  data() {
    return {
      isLoading: false,
      originalCampaign: {}
    }
  },

  computed: {
    ...mapState('common-entities', ['categories']),

    ...mapGetters('account', ['allowedCategories']),

    ...mapState('campaign', ['buyerCampaign']),

    ...mapGetters('auth', ['isMinimumInitialDepositRequirementSatisfied']),

    // The account's selectable categories, see getSelectableCategories.
    categoryList() {
      const categoryList = getSelectableCategories(this.allowedCategories, this.categories)
        .map(category => ({
          ...category,
          displayName: category.isCpaConversionOnly ? `${category.name} (CPA Only)` : category.name
        }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName))

      categoryList.unshift({ name: 'Please select', displayName: 'Please select', value: null })

      return categoryList
    },

    // The store holds THIS campaign, not a leftover from a previously viewed one.
    isLoaded() {
      return this.originalCampaign.id === this.id && this.buyerCampaign.id === this.id
    },

    // Guarded on a truthy id: the "Please select" placeholder has no id, so an unguarded lookup
    // would match it and report a selected category with no languages.
    selectedCategory() {
      return this.buyerCampaign.categoryId
        ? this.categoryList.find(category => category.id === this.buyerCampaign.categoryId) || null
        : null
    },

    // The API rejects a language the category is not configured for, so the choice is always
    // scoped to the selected category's languages.
    categoryLanguages() {
      return this.selectedCategory?.languages ?? []
    },

    // Exactly one language is not a choice - it is preselected and the dropdown is hidden.
    showLanguageSelect() {
      return Boolean(this.selectedCategory) && this.categoryLanguages.length !== 1
    },

    languageOptions() {
      const languageOptions = [...this.categoryLanguages]

      languageOptions.unshift({ name: 'Please select', value: null })

      return languageOptions
    },

    // Minimum Call Duration is the only change that can force an admin re-review here: Category,
    // Language, Country and Destination are all fixed, and Audience never triggers one (same rule
    // as the full Edit Campaign page).
    changedReviewSections() {
      if (!this.originalCampaign.id || this.buyerCampaign.status === 'InReview') {
        return []
      }

      const turnedOffMinimumDuration = this.originalCampaign.budget?.minimumDurationSelected === true
        && this.buyerCampaign.budget?.minimumDurationSelected === false

      return turnedOffMinimumDuration ? ['Minimum Call Duration'] : []
    },

    categoryId() {
      return this.buyerCampaign.categoryId
    },

    languageId() {
      return this.buyerCampaign.languageId
    }
  },

  // Deliberately no setDefaultBuyerCampaign() and no applySoftphoneDefaults(): every property
  // this page does not show is submitted exactly as it was fetched, so admin-side edits survive
  // a buyer save. Create-page defaults are only correct for a campaign that does not exist yet.
  async created() {
    await this.fetchBuyerCampaign(this.id)

    if (!isSoftphoneCampaign(this.buyerCampaign)) {
      this.$router.replace({ name: 'BuyerEditCampaign', params: { id: this.id } })

      return
    }

    // Deliberately NOT `document.title = this.buyerCampaign.name`, which the buyer's own edit page
    // does: a softphone campaign's name is generated and names the softphone in it, and this page
    // is the agent's. The static title from the mixin stands.
    await this.getCategories()

    this.originalCampaign = cloneDeep(this.buyerCampaign)
  },

  beforeMount() {
    window.addEventListener('beforeunload', this.preventNavigation)
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.preventNavigation)
  },

  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedChanges()) {
      if (window.confirm('You have unsaved changes. Do you confirm leaving the page?')) {
        next()
      } else {
        next(false)
      }
    } else {
      next()
    }
  },

  methods: {
    ...mapActions('common-entities', ['getCategories']),

    ...mapActions('campaign', ['editBuyerCampaign', 'fetchBuyerCampaign']),

    getBadgeVariantForCampaignStatus,

    getStatusText,

    preventNavigation(event) {
      if (this.hasUnsavedChanges()) {
        event.preventDefault()
        event.returnValue = ''
      }
    },

    showConfirmSubmit() {
      this.$bvModal.show('update-softphone-campaign-modal')
    },

    async submitForm() {
      if (!this.isLoaded) {
        return
      }

      try {
        this.isLoading = true

        await this.editBuyerCampaign(cloneDeep(this.buyerCampaign))

        this.trackMarketBudgetSaved({ original: this.originalCampaign, campaignId: this.id })

        showSuccessMessage('Campaign updated successfully')

        this.$bvModal.hide('update-softphone-campaign-modal')

        // Saved, so nothing is unsaved any more - set before leaving or the route guard would ask
        // the agent to confirm abandoning the changes it just wrote.
        this.originalCampaign = cloneDeep(this.buyerCampaign)

        // The list is where the campaign is acted on (Go Live, its status), and it fetches for
        // itself - staying on a form with nothing left to change was a dead end.
        this.$router.push({ name: 'ListBuyerCampaigns' })
      } catch (error) {
        showErrorMessage(error.response?.data?.title || error.message)
      } finally {
        this.isLoading = false
      }
    },

    hasUnsavedChanges() {
      return Boolean(this.originalCampaign.id) && !isEqual(this.originalCampaign, this.buyerCampaign)
    }
  }
}
</script>

<style lang="scss" scoped></style>
